import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Chip, Divider, Button } from '@material-ui/core';
import { Card, CardContent, Tooltip, IconButton } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { GetApp } from '@material-ui/icons';
import ImageChooserForm from './ImageChooserForm';
import { Actions as Acquisition } from '../../../store/ducks/acquisition';
import { Actions as ShoreS } from '../../../store/ducks/shoreS';
import { exportImagesAsCsv } from '../visualization/imageCsv';
import { readSelectedImages } from '../visualization/shoreS/utils';

import SimpleInfoDialog from '../dialog/SimpleInfoDialog';

const useStyles = makeStyles((theme) => ({
  item: {
    margin: 0,
    padding: 0,
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
}));

/**
 * @function ImageChooserCard
 * @description Component for choosing and loading satellite images.
 * Displays available images and allows users to load them into the application.
 * The listed dates can be exported as CSV and imported back on the vertical's image selection
 * step to restore the same selection.
 * @param {Object} props - Component props.
 * @param {string} props.className - Additional CSS class for styling.
 * @param {boolean} props.intertidalMode - Acquires images through the intertidal action.
 * @param {boolean} props.shoreSMode - Acquires images through the SHORE-S action and lists the
 * dates persisted in session storage instead of the ones in the acquisition state.
 * @param {string} props.vertical - Vertical slug, used to name the exported CSV file.
 * @returns {JSX.Element} Rendered ImageChooserCard component.
 */
const ImageChooserCard = ({ className, intertidalMode = false, shoreSMode = false, vertical = 'shoreline' }) => {
  const acquisitionAvailableDates = useSelector((state) => state.acquisition.availableDates);
  const satellite = useSelector((state) => state.acquisition.satellite, shallowEqual);
  const classes = useStyles();
  const dispatch = useDispatch();
  const [t] = useTranslation();

  const [showImageChooserHelp, setShowImageChooserHelp] = React.useState(false);

  const acquireAction = shoreSMode
    ? ShoreS.acquireImage
    : intertidalMode
      ? Acquisition.acquireImageIntertidal
      : Acquisition.acquireImage;

  const selectedImages = shoreSMode ? readSelectedImages() : [];
  const availableDates = shoreSMode ? selectedImages : acquisitionAvailableDates;

  return availableDates === undefined ? null : (
    <Card className={className} style={{ padding: 0 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0px 7px',
        }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <p style={{ fontSize: 16, fontWeight: 'bold', padding: 3 }}>{t('forms.imageChooser.title')}</p>
          <Tooltip title={t('forms.imageChooser.helpTooltip')}>
            <IconButton onClick={() => setShowImageChooserHelp(true)} size="small" style={{ padding: 4 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        <Chip className={classes.chip} label={`${availableDates.length} ${t('forms.imageChooser.resultQuantity')}`} />
      </div>

      <Divider />

      <CardContent style={{ padding: 15 }}>
        <Button
          variant="outlined"
          startIcon={<GetApp />}
          disabled={!availableDates.length}
          style={{ marginBottom: 8, fontSize: '12px' }}
          onClick={() => exportImagesAsCsv(availableDates, `${vertical}-selected-dates.csv`)}>
          {t('forms.imageChooser.exportDates')}
        </Button>
        <div id="imageChooserForm">
          <ImageChooserForm
            images={availableDates}
            disabledPredicate={(i) => false}
            onLoadRequested={(i) => dispatch(acquireAction(availableDates[i].name, availableDates[i].date))}
            formatter={(i) => satellite.get(availableDates[i].name).format}
          />
        </div>
      </CardContent>

      <SimpleInfoDialog
        open={showImageChooserHelp}
        close={() => setShowImageChooserHelp(false)}
        title="forms.imageChooser.helpTooltip"
        text="forms.imageChooser.helpContent"
      />
    </Card>
  );
};

export default ImageChooserCard;
